import { useState, useEffect, useCallback } from 'react';
import { schoolUserApi, testAdminApi } from '../api';

const ROLE_LABELS = { '3': 'Owner', '11': 'Student', '12': 'Teacher' };
const EMPTY_EDIT  = { address: '', contactnumber: '', password: '', bstatus: true };
const EMPTY_ASSIGN = { classId: '', classLabel: '', rollNumber: '' };

function roleLabel(id)      { return ROLE_LABELS[String(id)] || `Role ${id}`; }
function isStudent(roleId)  { return String(roleId) === '11'; }
function isOwner(roleId)   { return String(roleId) === '3'; }

function roleBadgeClass(id) {
  const r = String(id);
  if (r === '3')  return 'badge badge-blue';
  if (r === '12') return 'badge badge-green';
  if (r === '11') return 'badge badge-orange';
  return 'badge badge-gray';
}

export default function ViewEmployees() {
  const [users, setUsers]             = useState([]);
  const [classMap, setClassMap]       = useState({});   // userId → assignment row
  const [classes, setClasses]         = useState([]);   // sb.classes list
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState('');
  const [msg, setMsg]                 = useState(null);
  const [search, setSearch]           = useState('');

  // profile edit state
  const [editId, setEditId]           = useState(null);
  const [form, setForm]               = useState(EMPTY_EDIT);
  const [saving, setSaving]           = useState(false);
  const [showPwd, setShowPwd]         = useState(false);

  // class assignment state
  const [assignId, setAssignId]       = useState(null); // userId being assigned
  const [assignForm, setAssignForm]   = useState(EMPTY_ASSIGN);
  const [assignSaving, setAssignSaving] = useState(false);

  const loadAll = useCallback(() => {
    setLoading(true);
    setErr('');
    Promise.all([
      schoolUserApi.getUsers(),
      schoolUserApi.getStudentClasses(),
      testAdminApi.getClasses(),
    ]).then(([users, assignments, classes]) => {
      setUsers(Array.isArray(users) ? users : []);
      const map = {};
      (Array.isArray(assignments) ? assignments : []).forEach(a => { map[a.userId] = a; });
      setClassMap(map);
      setClasses(Array.isArray(classes) ? classes : []);
    }).catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setA = (k, v) => setAssignForm(f => ({ ...f, [k]: v }));

  // ── Profile edit ──────────────────────────────────────────────────────────
  const handleEdit = (u) => {
    setEditId(u.userId);
    setAssignId(null);
    setForm({ address: u.contactAddress || '', contactnumber: u.contactNumbers || '', password: '', bstatus: u.status, userName: u.userName, roleId: u.roleId });
    setShowPwd(false);
    setMsg(null);
  };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await schoolUserApi.updateUser(editId, form);
      if (res.error) { setMsg({ text: res.error, ok: false }); return; }
      window.location.reload();
    } catch (e) { setMsg({ text: e.message, ok: false }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete user "${name}" (${userId})? This cannot be undone.`)) return;
    try {
      const res = await schoolUserApi.deleteUser(userId);
      if (res.error) { setMsg({ text: res.error, ok: false }); return; }
      window.location.reload();
    } catch (e) { setMsg({ text: e.message, ok: false }); }
  };

  // ── Class assignment ──────────────────────────────────────────────────────
  const handleOpenAssign = (u) => {
    const existing = classMap[u.userId];
    setAssignId(u.userId);
    setEditId(null);
    setAssignForm({
      classId:    existing ? String(existing.classId) : '',
      classLabel: existing ? existing.classLabel : '',
      rollNumber: existing ? existing.rollNumber || '' : '',
      userName:   u.userName,
    });
    setMsg(null);
  };

  const handleClassSelect = (classId) => {
    const cls = classes.find(c => String(c.id) === String(classId));
    const label = cls ? (cls.section ? `${cls.name} - ${cls.section}` : cls.name) : '';
    setAssignForm(f => ({ ...f, classId, classLabel: label }));
  };

  const handleAssignSave = async () => {
    if (!assignForm.classId) { setMsg({ text: 'Please select a class', ok: false }); return; }
    setAssignSaving(true); setMsg(null);
    try {
      const res = await schoolUserApi.assignClass(assignId, assignForm);
      if (res.error) { setMsg({ text: res.error, ok: false }); return; }
      window.location.reload();
    } catch (e) { setMsg({ text: e.message, ok: false }); }
    finally { setAssignSaving(false); }
  };

  const handleRemoveAssignment = async (userId) => {
    if (!window.confirm('Remove class assignment for this student?')) return;
    try {
      const res = await schoolUserApi.removeClassAssignment(userId);
      if (res.error) { setMsg({ text: res.error, ok: false }); return; }
      window.location.reload();
    } catch (e) { setMsg({ text: e.message, ok: false }); }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.userName || '').toLowerCase().includes(s)
        || (u.userId   || '').toLowerCase().includes(s)
        || (u.contactNumbers || '').includes(s);
  });

  return (
    <div>
      {/* Filter bar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 20px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
              <i className="fas fa-search"></i>
              <input type="text" placeholder="Search by name, login ID or phone…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="btn btn-outline btn-sm" onClick={loadAll} title="Refresh">
              <i className="fas fa-sync-alt"></i> Refresh
            </button>
            <span style={{ fontSize: 13, color: '#718096' }}>{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {msg && (
        <div className={msg.ok ? 'success-msg' : 'error-msg'} style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <i className={`fas ${msg.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
          {msg.text}
        </div>
      )}
      {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}

      {/* Profile edit panel */}
      {editId && (
        <div className="card" style={{ marginBottom: 16, border: '2px solid #0066CC' }}>
          <div className="card-header" style={{ background: '#ebf8ff' }}>
            <div className="card-title" style={{ color: '#0066CC' }}>
              <i className="fas fa-user-edit" style={{ marginRight: 8 }}></i>
              Edit: <strong>{form.userName}</strong> ({editId})
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setEditId(null)}><i className="fas fa-times"></i> Cancel</button>
          </div>
          <div className="card-body">
            <div className="form-grid cols-2">
              <div className="form-group">
                <label className="form-label">📍 Contact Address</label>
                <textarea className="form-control" value={form.address} onChange={e => set('address', e.target.value)} rows={3} placeholder="Full address" />
              </div>
              <div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label">📱 Contact Number</label>
                  <input className="form-control" value={form.contactnumber} onChange={e => set('contactnumber', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number" maxLength={10} />
                </div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label">🔒 New Password <span style={{ color: '#a0aec0', fontWeight: 400 }}>(blank = keep)</span></label>
                  <div style={{ position: 'relative' }}>
                    <input className="form-control" type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} placeholder="New password (optional)" autoComplete="new-password" style={{ paddingRight: 40 }} />
                    <button type="button" onClick={() => setShowPwd(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#718096', fontSize: 13 }} tabIndex={-1}>
                      <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">🔘 Status</label>
                  <select className="form-control" value={form.bstatus ? 'true' : 'false'} onChange={e => set('bstatus', e.target.value === 'true')}>
                    <option value="true">ACTIVE</option>
                    <option value="false">DISABLE</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: 12 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setEditId(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                <i className="fas fa-save"></i> {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Class assignment panel */}
      {assignId && (
        <div className="card" style={{ marginBottom: 16, border: '2px solid #38a169' }}>
          <div className="card-header" style={{ background: '#f0fff4' }}>
            <div className="card-title" style={{ color: '#276749' }}>
              🏫 Assign Class — <strong>{assignForm.userName}</strong> ({assignId})
              {classMap[assignId] && (
                <span className="badge badge-green" style={{ marginLeft: 10, fontSize: 12 }}>
                  Currently: {classMap[assignId].classLabel}
                </span>
              )}
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setAssignId(null)}><i className="fas fa-times"></i> Cancel</button>
          </div>
          <div className="card-body">
            <div className="form-grid cols-3" style={{ alignItems: 'flex-end' }}>
              <div className="form-group">
                <label className="form-label">🏫 Class *</label>
                <select className="form-control" value={assignForm.classId} onChange={e => handleClassSelect(e.target.value)}>
                  <option value="">-- Select Class --</option>
                  {classes.map(c => {
                    const label = c.section ? `${c.name} - ${c.section}` : c.name;
                    return <option key={c.id} value={c.id}>{label}</option>;
                  })}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">🔢 Roll Number <span style={{ color: '#a0aec0', fontWeight: 400 }}>(optional)</span></label>
                <input className="form-control" value={assignForm.rollNumber} onChange={e => setA('rollNumber', e.target.value)} placeholder="e.g. 42" />
              </div>
              <div className="form-actions" style={{ paddingBottom: 2 }}>
                <button className="btn btn-primary btn-sm" onClick={handleAssignSave} disabled={assignSaving}>
                  <i className="fas fa-link"></i> {assignSaving ? 'Saving…' : (classMap[assignId] ? 'Update Class' : 'Assign Class')}
                </button>
                {classMap[assignId] && (
                  <button className="btn btn-danger btn-sm" onClick={() => handleRemoveAssignment(assignId)}>
                    <i className="fas fa-unlink"></i> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-users" style={{ marginRight: 8, color: '#0066CC' }}></i>
            Registered Users
          </div>
        </div>
        {loading ? <div className="loading-msg">Loading users…</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>👤 Full Name</th>
                  <th>🪪 Login ID</th>
                  <th>📞 Contact</th>
                  <th>🎭 Role</th>
                  <th>🏫 Class</th>
                  <th>🔢 Roll No</th>
                  <th>🔘 Status</th>
                  <th>📅 Registered</th>
                  <th>⚙️ Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: '#888', padding: 28 }}>
                    {users.length === 0 ? 'No users registered yet.' : 'No users match the search.'}
                  </td></tr>
                )}
                {filtered.map((u, i) => {
                  const assignment = classMap[u.userId];
                  return (
                    <tr key={u.userId} style={
                      editId === u.userId   ? { background: 'rgba(0,102,204,0.04)' } :
                      assignId === u.userId ? { background: 'rgba(56,161,105,0.04)' } : {}
                    }>
                      <td>{i + 1}</td>
                      <td><strong>{u.userName}</strong></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{u.userId}</td>
                      <td>{u.contactNumbers || '—'}</td>
                      <td><span className={roleBadgeClass(u.roleId)}>{roleLabel(u.roleId)}</span></td>
                      <td>
                        {isStudent(u.roleId) ? (
                          assignment
                            ? <span className="badge badge-green" style={{ fontSize: 12 }}>{assignment.classLabel}</span>
                            : <span style={{ color: '#a0aec0', fontSize: 12 }}>Not assigned</span>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: 13, color: '#718096' }}>
                        {isStudent(u.roleId) && assignment ? (assignment.rollNumber || '—') : '—'}
                      </td>
                      <td>
                        <span className={`badge ${u.status ? 'badge-green' : 'badge-red'}`}>
                          {u.status ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#718096', whiteSpace: 'nowrap' }}>{u.displayDate || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => editId === u.userId ? setEditId(null) : handleEdit(u)}
                            title="Edit profile"
                            style={editId === u.userId ? { borderColor: '#0066CC', color: '#0066CC' } : {}}
                          >
                            {editId === u.userId ? '✖️ Close' : '✏️ Edit'}
                          </button>
                          {isStudent(u.roleId) && (
                            <button
                              className="btn btn-sm"
                              onClick={() => assignId === u.userId ? setAssignId(null) : handleOpenAssign(u)}
                              title={assignment ? 'Change class' : 'Assign to class'}
                              style={{
                                background: assignId === u.userId ? '#c6f6d5' : assignment ? '#f0fff4' : '#ebf8ff',
                                border: `1px solid ${assignId === u.userId ? '#38a169' : assignment ? '#38a169' : '#0066CC'}`,
                                color: assignId === u.userId ? '#276749' : assignment ? '#276749' : '#0066CC',
                              }}
                            >
                              {assignId === u.userId ? '✖️ Cancel' : assignment ? '🏫 Class' : '🏫 Assign'}
                            </button>
                          )}
                          {!isOwner(u.roleId) && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(u.userId, u.userName)}
                              title="Delete user"
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
